import React, { useState } from "react";
import { Link as GatsbyLink, navigate } from "gatsby";
import { Global, css } from "@emotion/react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { isBoolean, isNull, trim } from "lodash";

import { ScreenReaderText } from "~/styles/screenReaderText";
import { getTextDecorationStyles } from "~/styles/helpers";

const getDisabledStyles = ({ disabled }) => {
    return css`
        pointer-events: ${disabled ? "none" : "inherit"};
        opacity: ${disabled ? "0.65" : "inherit"};
    `;
};

const StyledGatsbyLink = styled(GatsbyLink)`
    ${getDisabledStyles};
    ${getTextDecorationStyles};
`;

const AnchorLink = styled.a`
    ${getDisabledStyles};
    ${getTextDecorationStyles};
`;

const GatsbyJumpLink = ({ onClick, href, tagRef, ...rest }) => {
    const [smoothScroll, setSmoothScroll] = useState(false);

    return (
        <>
            {smoothScroll ? (
                <Global
                    styles={css`
                        html {
                            scroll-behavior: smooth;
                        }
                    `}
                />
            ) : null}
            <AnchorLink
                {...rest}
                href={href}
                ref={tagRef}
                onClick={(event) => {
                    event.preventDefault();
                    setSmoothScroll(true);
                    navigate(href);
                    if (onClick) {
                        onClick(event);
                    }
                }}
            />
        </>
    );
};

const isRelativeLink = (linkTo) => {
    return new RegExp(`^(/(?!/))`).test(linkTo);
};

const isHashLink = (linkTo) => {
    return /(#[a-zA-Z0-9_=-]*)/.test(linkTo);
};

const isExternalLink = ({ linkTo, escapedFrontendURL, external }) => {
    if (external) {
        return true;
    }

    if (isRelativeLink(linkTo)) {
        return false;
    }

    return !linkTo.startsWith(escapedFrontendURL);
};

const shouldOpenInNewTab = (props) => {
    const { newTabOverride } = props;

    switch (newTabOverride) {
        case "newTab":
            return true;
        case "sameTab":
            return false;
        default:
            return isExternalLink(props);
    }
};

const shouldUseGatsbyJumpLink = (props) => {
    const { linkTo } = props;

    return !shouldOpenInNewTab(props) && isHashLink(linkTo);
};

const shouldUseGatsbyLink = (props) => {
    const { linkTo } = props;

    if (shouldOpenInNewTab(props)) {
        return false;
    }

    return isRelativeLink(linkTo) && !isHashLink(linkTo);
};

const formatProps = (props) => {
    const {
        linkTo,
        className = "",
        onClick = () => {},
        ariaExpanded = null,
        ariaLabel = "",
        ariaHidden = false,
        tagRef = null,
        onKeyDown = () => {},
        topLinkLabel = null,
        state = {},
        lastItem = false,
        title = "",
        location = {},
        ariaLabelledby = "",
        target = "_blank",
    } = props;

    const genericProps = { className, onClick, onKeyDown };

    if (shouldUseGatsbyJumpLink(props)) {
        genericProps.tagRef = tagRef;
    } else {
        genericProps.ref = tagRef;
    }

    if (ariaLabel) {
        genericProps["aria-label"] = ariaLabel;
    }

    if (ariaHidden) {
        genericProps["aria-hidden"] = ariaHidden;
    }

    if (ariaLabelledby) {
        genericProps["aria-labelledby"] = ariaLabelledby;
    }

    if (title) {
        genericProps["title"] = title;
    }

    if (!isNull(ariaExpanded)) {
        genericProps["aria-expanded"] = ariaExpanded;
    }

    if (!isNull(topLinkLabel)) {
        genericProps["data-parent"] = topLinkLabel;
        genericProps["data-last"] = lastItem;
    }

    if (!shouldUseGatsbyJumpLink(props)) {
        genericProps["ref"] = tagRef;
    } else {
        genericProps["tagRef"] = tagRef;
    }

    if (shouldUseGatsbyLink(props)) {
        if (location?.pathname && location?.pathname === linkTo) {
            genericProps["aria-current"] = "page";
        } else if (
            location?.pathname &&
            linkTo === "/" &&
            location?.pathname !== linkTo
        ) {
            genericProps["aria-current"] = "false";
        }

        return {
            to: linkTo,
            state: { ...state, isGatsbyLink: true },
            ...genericProps,
        };
    } else {
        const newTabAttributes = {};
        if (shouldOpenInNewTab(props)) {
            newTabAttributes.target = target;
            newTabAttributes.rel = "noopener noreferrer";
        }
        return {
            href: linkTo,
            ...newTabAttributes,
            ...genericProps,
        };
    }
};

const BaseLink = ({
    to,
    screenReaderText = "",
    children,
    disabled,
    showTextDecoration = false,
    targetElement = "",
    ...rest
}) => {
    const escapedFrontendURL = trim(process.env.GATSBY_SITE_URL);
    const linkTo = trim(to) || `${escapedFrontendURL}/`;
    const updatedProps = { linkTo, escapedFrontendURL, ...rest };
    const formattedProps = formatProps(updatedProps);
    let Link;

    if (shouldUseGatsbyLink(updatedProps)) {
        Link = StyledGatsbyLink;
    } else if (shouldUseGatsbyJumpLink(updatedProps)) {
        Link = GatsbyJumpLink;
    } else {
        Link = AnchorLink;
    }

    return (
        <Link
            {...formattedProps}
            disabled={disabled}
            showtextdecoration={
                isBoolean(showTextDecoration)
                    ? showTextDecoration.toString()
                    : "false"
            }
            targetelement={targetElement}
            aria-disabled={disabled}
            tabIndex={disabled ? "-1" : null}
        >
            {children}
            {screenReaderText ? (
                <ScreenReaderText>{screenReaderText}</ScreenReaderText>
            ) : null}
        </Link>
    );
};

BaseLink.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    external: PropTypes.bool,
    to: PropTypes.string.isRequired,
    newTabOverride: PropTypes.string,
    onClick: PropTypes.func,
    ariaExpanded: PropTypes.bool,
    ariaLabel: PropTypes.string,
    screenReaderText: PropTypes.string,
    onKeyDown: PropTypes.func,
    tagRef: PropTypes.object,
    topLinkLabel: PropTypes.string,
    state: PropTypes.object,
    lastItem: PropTypes.bool,
    title: PropTypes.string,
    location: PropTypes.shape({}),
    ariaLabelledby: PropTypes.string,
    targetElement: PropTypes.string,
    showTextDecoration: PropTypes.bool,
};

export default BaseLink;
